import type {
  AdminRoundDetailResponse,
  AdminRoundSummary,
  AuthenticatedAdmin,
  RngChainVerification,
} from "@utopia-well-done-ltd/contracts";
import { type FormEvent, useCallback, useEffect, useRef, useState } from "react";
import { ApiError, api } from "../api/client.js";
import { can } from "../capabilities.js";
import { ActivityTimeline } from "../components/ActivityTimeline.js";
import { BlackjackRound } from "../components/BlackjackRound.js";
import {
  ActiveFilters,
  CopyButton,
  CsvExportButton,
  DatePresets,
  detailUrl,
  LoadingState,
  ResultCount,
  RetryBanner,
  readDetailId,
  readUrlFilters,
  toIsoOrUndefined,
  writeDetailId,
  writeUrlFilters,
} from "../components/ExplorerTools.js";
import { PokerRound } from "../components/PokerRound.js";
import { asAmount, asMoment } from "../format.js";
import { rememberRecent } from "../operator-memory.js";

interface Filters {
  from?: string;
  game?: string;
  player?: string;
  roundId?: string;
  status?: string;
  table?: string;
  to?: string;
}

const emptyForm = { from: "", game: "", player: "", roundId: "", status: "", table: "", to: "" };
const start = (date: string) => toIsoOrUndefined(`${date}T00:00:00.000Z`);
const end = (date: string) => toIsoOrUndefined(`${date}T23:59:59.999Z`);
const asFilters = (form: typeof emptyForm): Filters => {
  const from = start(form.from);
  const to = end(form.to);
  return {
    ...(from ? { from } : {}),
    ...(form.game ? { game: form.game } : {}),
    ...(form.player.trim() ? { player: form.player.trim() } : {}),
    ...(form.roundId.trim() ? { roundId: form.roundId.trim() } : {}),
    ...(form.status ? { status: form.status } : {}),
    ...(form.table.trim() ? { table: form.table.trim() } : {}),
    ...(to ? { to } : {}),
  };
};

export function Rounds({ admin }: { admin: AuthenticatedAdmin }) {
  const [rounds, setRounds] = useState<AdminRoundSummary[] | null>(null);
  const [filters, setFilters] = useState<Filters>({});
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [detail, setDetail] = useState<AdminRoundDetailResponse | null>(null);
  const dialog = useRef<HTMLDialogElement>(null);
  const [chain, setChain] = useState<RngChainVerification | null>(null);
  const detailGeneration = useRef(0);
  const loadGeneration = useRef(0);
  const initialForm = useRef(readUrlFilters(emptyForm)).current;
  const initialDetail = useRef(readDetailId()).current;
  const [form, setForm] = useState(initialForm);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  const load = useCallback(async (active: Filters, before?: string) => {
    const requestGeneration = ++loadGeneration.current;
    if (!before) {
      detailGeneration.current += 1;
      setDetail(null);
      setChain(null);
    }
    setPending(true);
    setError(null);
    try {
      const page = await api.rounds({ ...active, ...(before ? { before } : {}) });
      if (loadGeneration.current !== requestGeneration) return;
      setRounds((current) => (before ? [...(current ?? []), ...page.rounds] : page.rounds));
      setNextCursor(page.nextCursor);
    } catch (caught) {
      if (loadGeneration.current !== requestGeneration) return;
      setError(caught instanceof ApiError ? caught.message : "Could not load rounds.");
    } finally {
      if (loadGeneration.current === requestGeneration) setPending(false);
    }
  }, []);

  useEffect(() => {
    if (!detail) return;
    if (dialog.current?.showModal && !dialog.current.open) dialog.current.showModal();
    else dialog.current?.setAttribute("open", "");
    document.body.classList.add("modal-open");
    return () => document.body.classList.remove("modal-open");
  }, [detail]);

  function apply(event: FormEvent) {
    event.preventDefault();
    const next = asFilters(form);
    writeUrlFilters(form);
    setFilters(next);
    writeDetailId(null);
    void load(next);
  }

  const inspect = useCallback(
    async (roundId: string, updateUrl = true) => {
      const requestGeneration = ++detailGeneration.current;
      setPending(true);
      setError(null);
      setChain(null);
      try {
        const [found, rngChain] = await Promise.all([
          api.round(roundId),
          can(admin.role, "audit.read") ? api.rngChain() : Promise.resolve(null),
        ]);
        if (detailGeneration.current !== requestGeneration) return;
        setDetail(found);
        setChain(rngChain);
        if (updateUrl) writeDetailId(roundId);
        rememberRecent(admin.id, {
          id: `round:${roundId}`,
          label: `Round ${roundId.slice(0, 8)}`,
          search: `?screen=rounds&detail=${encodeURIComponent(roundId)}`,
        });
      } catch (caught) {
        if (detailGeneration.current !== requestGeneration) return;
        setError(caught instanceof ApiError ? caught.message : "Could not inspect the round.");
      } finally {
        if (detailGeneration.current === requestGeneration) setPending(false);
      }
    },
    [admin.id, admin.role],
  );

  useEffect(() => {
    const initial = asFilters(initialForm);
    setFilters(initial);
    void load(initial);
    if (initialDetail) void inspect(initialDetail, false);
  }, [initialDetail, initialForm, inspect, load]);

  function closeDetail() {
    detailGeneration.current += 1;
    setPending(false);
    setDetail(null);
    setChain(null);
    writeDetailId(null);
  }

  function clear() {
    setForm(emptyForm);
    writeUrlFilters(emptyForm);
    setFilters({});
    writeDetailId(null);
    void load({});
  }

  function removeFilter(key: string) {
    const next = { ...filters };
    delete next[key as keyof Filters];
    setFilters(next);
    if (key in form) setForm({ ...form, [key]: "" });
    writeUrlFilters({ ...form, [key]: "" });
    writeDetailId(null);
    void load(next);
  }

  const activeFilters = Object.entries(filters).map(([key, value]) => ({
    key,
    label: key === "roundId" ? "Round" : key,
    value: key === "from" || key === "to" ? value.slice(0, 10) : value,
  }));

  return (
    <section className="body">
      <form className="transaction-filters round-filters" onSubmit={apply}>
        <label className="field">
          <span>Round ID</span>
          <input
            className="input mono-12"
            onChange={(event) => setForm({ ...form, roundId: event.target.value })}
            placeholder="Exact UUID"
            value={form.roundId}
          />
        </label>
        <label className="field">
          <span>Player</span>
          <input
            className="input"
            onChange={(event) => setForm({ ...form, player: event.target.value })}
            placeholder="ID, email or display name"
            value={form.player}
          />
        </label>
        <label className="field">
          <span>Table</span>
          <input
            className="input"
            onChange={(event) => setForm({ ...form, table: event.target.value })}
            placeholder="Table prefix"
            value={form.table}
          />
        </label>
        <label className="field">
          <span>Game</span>
          <select
            className="input"
            onChange={(event) => setForm({ ...form, game: event.target.value })}
            value={form.game}
          >
            <option value="">All games</option>
            <option value="blackjack">Blackjack</option>
            <option value="poker">Poker</option>
            <option value="roulette">Roulette</option>
            <option value="slots">Slots</option>
          </select>
        </label>
        <label className="field">
          <span>Status</span>
          <select
            className="input"
            onChange={(event) => setForm({ ...form, status: event.target.value })}
            value={form.status}
          >
            <option value="">All statuses</option>
            <option value="open">Open</option>
            <option value="settled">Settled</option>
            <option value="abandoned">Abandoned</option>
          </select>
        </label>
        <label className="field">
          <span>From</span>
          <input
            className="input"
            onChange={(event) => setForm({ ...form, from: event.target.value })}
            type="date"
            value={form.from}
          />
        </label>
        <label className="field">
          <span>To</span>
          <input
            className="input"
            onChange={(event) => setForm({ ...form, to: event.target.value })}
            type="date"
            value={form.to}
          />
        </label>
        <DatePresets onSelect={(range) => setForm({ ...form, ...range })} />
        <div className="transaction-filter-actions">
          <button className="btn" disabled={pending} type="submit">
            Apply filters
          </button>
          <button className="btn btn-quiet" disabled={pending} onClick={clear} type="button">
            Clear
          </button>
        </div>
      </form>

      <ActiveFilters filters={activeFilters} onClear={clear} onRemove={removeFilter} />

      {error ? <RetryBanner message={error} onRetry={() => void load(filters)} /> : null}

      {rounds ? (
        <div className="result-tools">
          <ResultCount count={rounds.length} noun="round" />
          <CsvExportButton
            filename="rounds.csv"
            headers={[
              "Started at",
              "Round ID",
              "Game",
              "Players",
              "Table",
              "Status",
              "Stake",
              "Currency",
            ]}
            rows={rounds.map((round) => [
              round.startedAt,
              round.id,
              round.game,
              round.players.map((player) => player.displayName ?? player.email).join(", "),
              round.tableId,
              round.status,
              round.stake,
              round.currency,
            ])}
          />
        </div>
      ) : null}
      <div className="transaction-layout">
        <div className="card table-scroll">
          {!rounds ? (
            <LoadingState label="Loading game rounds…" />
          ) : rounds.length === 0 ? (
            <div className="empty empty-action">
              <p>No round matches these filters.</p>
              <button className="btn btn-quiet" onClick={clear} type="button">
                Clear filters
              </button>
            </div>
          ) : (
            <table className="mobile-cards rounds-table">
              <thead>
                <tr>
                  <th scope="col">Started</th>
                  <th scope="col">Game</th>
                  <th scope="col">Players</th>
                  <th scope="col">Table</th>
                  <th scope="col">Status</th>
                  <th scope="col" style={{ textAlign: "right" }}>
                    Stake
                  </th>
                </tr>
              </thead>
              <tbody>
                {rounds.map((round) => (
                  <tr
                    aria-label={`Open ${round.game} round ${round.id}`}
                    className={`clickable-row${detail?.round.id === round.id ? " selected" : ""}`}
                    key={round.id}
                    onClick={() => void inspect(round.id)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        void inspect(round.id);
                      }
                    }}
                    tabIndex={0}
                  >
                    <td className="label-13 dim" data-label="Started">
                      {asMoment(round.startedAt)}
                    </td>
                    <td data-label="Game">
                      <span className="cell-main">
                        <span className="label-13">{round.game}</span>
                        <span className="mono-12 faint">{round.id}</span>
                      </span>
                    </td>
                    <td className="label-13" data-label="Players">
                      {round.players.map((player) => player.displayName ?? player.email).join(", ")}
                    </td>
                    <td className="mono-12 dim" data-label="Table">
                      {round.tableId ?? "—"}
                    </td>
                    <td data-label="Status">
                      <span className={`badge badge-${round.status}`}>{round.status}</span>
                    </td>
                    <td className="mono-12" data-label="Stake" style={{ textAlign: "right" }}>
                      {asAmount({ amount: round.stake, currency: round.currency })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {nextCursor ? (
            <button
              className="btn btn-quiet load-more-button"
              disabled={pending}
              onClick={() => void load(filters, nextCursor)}
              type="button"
            >
              {pending ? "Loading…" : "Load more"}
            </button>
          ) : null}
        </div>

        {detail ? (
          <dialog
            aria-labelledby="global-round-detail-title"
            className="round-dialog round-detail"
            onCancel={closeDetail}
            onClick={(event) => {
              if (event.target === event.currentTarget) closeDetail();
            }}
            onKeyDown={(event) => {
              if (event.key === "Escape") closeDetail();
            }}
            ref={dialog}
          >
            <div className="round-dialog-head">
              <div>
                <h2 className="heading-20" id="global-round-detail-title">
                  Round detail
                </h2>
                <p className="mono-12 faint">{detail.round.id}</p>
              </div>
              <div className="round-dialog-actions">
                <button
                  aria-label="Close round detail"
                  className="btn btn-quiet btn-sm"
                  onClick={closeDetail}
                  type="button"
                >
                  Close
                </button>
                <CopyButton label="Copy link" value={detailUrl("rounds", detail.round.id)} />
              </div>
            </div>
            <div className="round-dialog-body">
              <div className="round-integrity">
                <span className="round-integrity-item">
                  Integrity
                  <strong
                    className={`badge badge-${detail.verification.verified ? "active" : "banned"}`}
                  >
                    {detail.verification.verified
                      ? "Verified"
                      : (detail.verification.reason ?? "Not verified")}
                  </strong>
                </span>
                {chain && detail.round.game !== "poker" ? (
                  <span className="round-integrity-item">
                    RNG chain
                    <strong className={`badge badge-${chain.intact ? "active" : "banned"}`}>
                      {chain.intact ? `Intact (${chain.checked})` : `Broken at ${chain.brokenAt}`}
                    </strong>
                  </span>
                ) : null}
              </div>
              {detail.round.game === "blackjack" ? (
                <BlackjackRound terminalState={detail.round.terminalState} />
              ) : null}
              {detail.round.game === "poker" ? (
                <PokerRound terminalState={detail.round.terminalState} />
              ) : null}
              <details className="round-technical-details">
                <summary>Integrity and technical details</summary>
                <dl>
                  <dt>Round</dt>
                  <dd className="id-with-copy">
                    <span className="mono-12">{detail.round.id}</span>
                    <CopyButton value={detail.round.id} />
                  </dd>
                  <dt>Engine</dt>
                  <dd>{detail.round.engineVersion}</dd>
                  <dt>Ruleset</dt>
                  <dd className="mono-12">{detail.round.rulesetId ?? "—"}</dd>
                  <dt>Artifact SHA-256</dt>
                  <dd className="mono-12 break-all">{detail.round.engineArtifactSha256 ?? "—"}</dd>
                  <dt>RNG commitment</dt>
                  <dd className="mono-12 break-all">{detail.rng?.seedCommit ?? "—"}</dd>
                  <dt>RNG reveal</dt>
                  <dd className="mono-12 break-all">{detail.rng?.seedReveal ?? "Not revealed"}</dd>
                </dl>
              </details>
              <div className="round-detail-section">
                <strong>Wagers</strong>
                {detail.wagers.map((wager) => (
                  <div
                    className="round-detail-row"
                    key={`${wager.player.id}:${wager.player.seatId ?? "solo"}`}
                  >
                    <span>
                      {wager.player.seatId ?? wager.player.email}
                      {wager.player.blackjackHandRole
                        ? ` · ${wager.player.blackjackHandRole === "primary" ? "Hand 1" : "Hand 2"}`
                        : ""}
                    </span>
                    <span className="mono-12">
                      {asAmount({ amount: wager.stake, currency: detail.round.currency })} →{" "}
                      {wager.payout === null
                        ? "open"
                        : asAmount({ amount: wager.payout, currency: detail.round.currency })}
                    </span>
                  </div>
                ))}
              </div>
              <div className="round-detail-section">
                <strong>Settlements</strong>
                {detail.settlements.length ? (
                  detail.settlements.map((entry) => (
                    <div className="round-detail-row" key={entry.id}>
                      <span>{entry.kind}</span>
                      <span className="mono-12">{entry.id}</span>
                    </div>
                  ))
                ) : (
                  <p className="empty">No settlement movements.</p>
                )}
              </div>
              <div className="round-detail-section">
                <strong>Actions</strong>
                {detail.actions.length ? (
                  detail.actions.map((action) => (
                    <div className="round-detail-row" key={action.sequence}>
                      <span>
                        #{action.sequence} {action.action}
                      </span>
                      <span>
                        {action.accepted === false ? "Rejected" : (action.seatId ?? "System")}
                      </span>
                    </div>
                  ))
                ) : (
                  <p className="empty">No actions recorded.</p>
                )}
              </div>
              {can(admin.role, "audit.read") ? (
                <ActivityTimeline targetId={detail.round.id} />
              ) : null}
            </div>
          </dialog>
        ) : null}
      </div>
    </section>
  );
}
